import 'requestidlecallback-polyfill'; // NOTE: Required for Safari support.

interface ThrottleOptions {
  leading?: boolean;
  trailing?: boolean;
}

/**
 * Creates a performance-optimized throttled function that matches lodash's throttle interface.
 * Uses requestIdleCallback for better performance and reduced UI jank.
 *
 * @template Args - The tuple type representing the argument types of the function
 * @template R - The return type of the original function
 *
 * @param {(...args: Args) => R} fn - The function to throttle
 * @param {number} [wait=0] - The number of milliseconds to throttle invocations to
 * @param {ThrottleOptions} [options] - The configuration options
 * @param {boolean} [options.leading=true] - Specify invoking on the leading edge of the timeout
 * @param {boolean} [options.trailing=true] - Specify invoking on the trailing edge of the timeout
 *
 * @returns {((...args: Args) => void) & { cancel: () => void; flush: () => void }}
 *          A throttled function that:
 *          - Takes the same arguments as the original function
 *          - Returns void (as execution may be delayed)
 *          - Has a `cancel` method to cancel delayed invocations
 *          - Has a `flush` method to immediately invoke any delayed execution
 *
 * @example
 * ```typescript
 * const throttledFn = runWhenIdle(
 *   (x: number, y: string) => console.log(x, y),
 *   1000,
 *   { leading: true, trailing: false }
 * );
 *
 * throttledFn(1, "hello"); // Executes immediately
 * throttledFn(2, "world"); // Ignored until idle callback
 * throttledFn.cancel();    // Cancels pending execution
 * ```
 */
export default function runWhenIdle<Args extends unknown[], R>(
  fn: (...args: Args) => R, // The function to throttle.
  wait = 0, // The number of milliseconds to throttle the function to.
  options: ThrottleOptions = {
    leading: true, // Whether to execute the function immediately if it is called before the wait time has elapsed.
    trailing: true, // Whether to execute the function after the wait time has elapsed.
  }, // The options for the throttle.
): ((...args: Args) => void) & {
  cancel: () => void; // Cancel the throttle.
  flush: () => void; // Execute the function immediately.
} {
  const { leading = true, trailing = true } = options;

  let idleCallbackId: number | undefined;
  let hasExecuted = false;
  let lastArgs: Args | undefined;

  const throttled = (...args: Args) => {
    lastArgs = args;

    if (!hasExecuted && leading) {
      // Execute immediately on first call if leading is true
      fn.apply(null, args);
      hasExecuted = true;
      return;
    }

    if (idleCallbackId) {
      window.cancelIdleCallback(idleCallbackId);
    }

    if (trailing) {
      idleCallbackId = window.requestIdleCallback(
        () => {
          if (lastArgs) {
            fn.apply(null, lastArgs);
          }
          idleCallbackId = undefined;
          hasExecuted = false;
        },
        { timeout: wait },
      );
    }
  };

  throttled.cancel = () => {
    if (idleCallbackId) {
      window.cancelIdleCallback(idleCallbackId);
      idleCallbackId = undefined;
    }
    hasExecuted = false;
    lastArgs = undefined;
  };

  throttled.flush = () => {
    if (idleCallbackId) {
      window.cancelIdleCallback(idleCallbackId);
      idleCallbackId = undefined;
      if (lastArgs) {
        fn.apply(null, lastArgs);
      }
      hasExecuted = false;
    }
  };

  return throttled as ((...args: Args) => void) & { cancel: () => void; flush: () => void };
}
